import { ArtworkImage } from './ArtworkImage';
import { useRiftArtworkPath } from '../utils/riftArtwork';
import { useGateBackPath } from './GateArtwork';
import React, { useEffect, useLayoutEffect, useId, useRef, useState } from 'react';
import { GameState, Investigator } from '../api';
import { ancientOneCounters, boardCounts, enabledBoards, investigatorConditions, investigatorSkills } from '../utils/gamePresentation';
import { getStatColor } from '../utils/statColor';
import { getDifficultyPresentation } from '../utils/difficultyPresentation';
import { observeElementResize } from './observeElementResize';
import { useMonsterTokenStyle } from '../utils/clientSettings';
import { getCardBackPath } from '../utils/cardBackArtwork';
import { getMonsterReworkImagePath } from './MonsterTokenSvg';

const trackArtwork: Record<string, string> = {
  Terror: '/TerrorTrack.png',
  Seals: '/ElderSign.jpg',
  Monsters: '/icons/Monster.jpg',
  Outskirts: '/icons/Outskirts.jpg',
  Dunwich: '/dunwhich_terror.jpg',
  'Deep One': '/deepOneRisingToken.jpg',
  Feds: '/Clue.png',
};

const circularTrackArtwork = new Set(['Gates', 'Seals', 'Deep One', 'Feds']);

const digitalTrackArtwork: Record<string, string> = {
  Monsters: getCardBackPath('monsters', 'Digital'),
  Outskirts: '/Locations/rework/small/Outskirts.jpg',
  Dunwich: getMonsterReworkImagePath('The Dunwich Horror'),
  'Deep One': getMonsterReworkImagePath('Deep One'),
};

const compactSkills = (investigator: Investigator) => investigatorSkills.map(([key, label]) => {
    const breakdown = investigator[`${key}Breakdown`];
    const total = breakdown?.total ?? investigator[key] ?? 0;
    const base = breakdown?.base ?? investigator[key] ?? 0;
    return { key, label, total, color: total > base ? '#3fb950' : total < base ? '#ff6b6b' : undefined };
  });

export const InvestigatorCompactStatus: React.FC<{ investigator: Investigator; showMoney?: boolean; decorative?: boolean; uppercaseSkillNames?: boolean }> = ({ investigator, showMoney = true, decorative = false, uppercaseSkillNames = false }) => (
  <span className="game-inspect-mobile" aria-hidden={decorative || undefined}>
    <span className="game-inspect-name">{investigator.name}</span>
    <span className="game-inspect-resources">
      <span className="quick-stat-stamina" aria-label={`Stamina ${investigator.stamina ?? 0} of ${investigator.maxStamina ?? 0}`}>{investigator.stamina ?? 0}/<span style={{ color: getStatColor(investigator.maxStaminaBreakdown) }}>{investigator.maxStamina ?? 0}</span></span>
      <span className="quick-stat-sanity" aria-label={`Sanity ${investigator.sanity ?? 0} of ${investigator.maxSanity ?? 0}`}>{investigator.sanity ?? 0}/<span style={{ color: getStatColor(investigator.maxSanityBreakdown) }}>{investigator.maxSanity ?? 0}</span></span>
      <span className="quick-stat-clue" aria-label={`Clues ${investigator.clues ?? 0}`}>{investigator.clues ?? 0}</span>
      {showMoney && <span className="quick-stat-money">{investigator.money ?? 0}</span>}
    </span>
    <span className="game-inspect-skills">{compactSkills(investigator).map(skill => <span key={skill.key}>{uppercaseSkillNames ? skill.label.toUpperCase() : skill.label} <b style={{ color: skill.color }}>{skill.total}</b></span>)}</span>
  </span>
);

const InvestigatorInspectButton: React.FC<{ investigator: Investigator; onOpen: () => void }> = ({ investigator, onOpen }) => {
  const descriptionId = useId();
  const skills = compactSkills(investigator);
  return <>
    <button type="button" className="game-investigator-inspect" onClick={onOpen}
      aria-label={`View ${investigator.name} details`} aria-describedby={descriptionId}>
      <InvestigatorCompactStatus investigator={investigator} decorative uppercaseSkillNames />
    </button>
    <span id={descriptionId} className="sr-only">Stamina {investigator.stamina ?? 0} of {investigator.maxStamina ?? 0}; Sanity {investigator.sanity ?? 0} of {investigator.maxSanity ?? 0}; Clues {investigator.clues ?? 0}; Money {investigator.money ?? 0}; {skills.map(skill => `${skill.key[0].toUpperCase()}${skill.key.slice(1)} ${skill.total}`).join('; ')}</span>
  </>;
};

export const InvestigatorSummary: React.FC<{ investigator: Investigator; locations?: GameState['locations'] }> = ({ investigator, locations }) => (
  <>
    <span className="game-resource-line">
      <span>Sta <b>{investigator.stamina ?? 0}/{investigator.maxStaminaBreakdown?.total ?? (investigator.maxStamina || investigator.stamina)}</b></span>
      <span>San <b>{investigator.sanity ?? 0}/{investigator.maxSanityBreakdown?.total ?? (investigator.maxSanity || investigator.sanity)}</b></span>
      <span>Money <b>${investigator.money ?? 0}</b></span><span>Clues <b>{investigator.clues ?? 0}</b></span>
    </span>
    <span className="game-skill-line">{investigatorSkills.map(([key, label]) => {
      const breakdown = investigator[`${key}Breakdown`];
      return <span key={key} title={key}>{label} <b className={breakdown && breakdown.total !== breakdown.base ? 'game-modified-stat' : ''}>{investigator.maxStamina > 0 ? breakdown?.total ?? investigator[key] ?? 0 : `${investigator[`${key}Min`] ?? 0}–${investigator[`${key}Max`] ?? 0}`}</b></span>;
    })}</span>
    {investigatorConditions(investigator, locations).length > 0 && <span className="game-condition-line">{investigatorConditions(investigator, locations).join(' · ')}</span>}
  </>
);

export const GameStatusSummary: React.FC<{
  state: GameState; investigator?: Investigator; playerName: string;
  onInvestigators: () => void; onAncientOne: () => void; onTracks: () => void;
  part?: 'all' | 'status' | 'tracks' | 'investigator';
  tracksExpanded?: boolean; onToggleTracks?: () => void;
  collapsible?: boolean;
  mobileActions?: { pending: boolean; open?: boolean; onOpen: () => void; message?: string };
}> = ({ state, investigator, playerName, onInvestigators, onAncientOne, onTracks, part = 'all', tracksExpanded, onToggleTracks, collapsible = true, mobileActions }) => {
  const gateBackPath = useGateBackPath();
  const artworkStyle = useMonsterTokenStyle();
  const riftArtwork = useRiftArtworkPath();
  const tracksId = useId();
  const [localExpanded, setLocalExpanded] = useState(false);
  const expanded = !collapsible || (tracksExpanded ?? localExpanded);
  const collapsed = !!state.ancientOne && !expanded;
  const tracksRef = useRef<HTMLDivElement>(null);
  const [sealsFit, setSealsFit] = useState(false);
  useLayoutEffect(() => {
    const row = tracksRef.current;
    if (!collapsed || !row) return;
    const mapFrame = row.closest<HTMLElement>('.game-map-frame');
    const measure = () => {
      const buttons = Array.from(row.children).filter((child): child is HTMLElement => child instanceof HTMLElement && child.tagName === 'BUTTON');
      const required = buttons.reduce((sum, button) => {
        if (button.classList.contains('ancient-summary')) {
          const style = getComputedStyle(button);
          const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight) + 2;
          // The name can ellipsize, but the complete doom value must fit.
          return sum + (button.querySelector('b')?.scrollWidth ?? 0) + padding;
        }
        return sum + button.getBoundingClientRect().width;
      }, 0) + Math.max(0, buttons.length - 1) * (parseFloat(getComputedStyle(row).columnGap) || 0);
      // A content-sized overlay gets narrower when Seals is hidden. Measure
      // available map space so it can show again when the viewport grows.
      const summary = row.parentElement;
      const chrome = summary ? summary.offsetWidth - row.clientWidth : 0;
      const available = mapFrame ? mapFrame.clientWidth - 8 - chrome : row.clientWidth;
      const constrained = row.style.width ? Math.min(available, parseFloat(row.style.width)) : available;
      setSealsFit(constrained >= required && constrained > 0);
    };
    measure();
    const disconnect = observeElementResize([row, ...(mapFrame ? [mapFrame] : []), ...Array.from(row.querySelectorAll('button'))], measure);
    window.addEventListener('resize', measure);
    document.fonts?.addEventListener('loadingdone', measure);
    return () => { disconnect(); window.removeEventListener('resize', measure); document.fonts?.removeEventListener('loadingdone', measure); };
  }, [collapsed, part, state.ancientOne, state.terrorLevel, state.maximumGatesAllowed, state.locations]);
  const investigatorRef = useRef<HTMLDivElement>(null);
  const [flowInvestigator, setFlowInvestigator] = useState(false);
  useEffect(() => {
    if (part !== 'all' && part !== 'investigator') return;
    let overviewHeight = '';
    const measure = () => {
      const element = investigatorRef.current;
      const height = element?.getBoundingClientRect().height || 0;
      const mobile = window.matchMedia?.('(max-width: 767px), (max-width: 1024px) and (max-height: 500px)').matches ?? false;
      const visible = element && window.getComputedStyle(element).display !== 'none';
      const flow = height > window.innerHeight * 0.4;
      setFlowInvestigator(prev => (prev === flow ? prev : flow));
      const nextHeight = `${mobile && visible && !flow ? height : 0}px`;
      if (nextHeight === overviewHeight) return;
      overviewHeight = nextHeight;
      document.documentElement.style.setProperty('--game-overview-height', nextHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    const disconnect = observeElementResize(investigatorRef.current, measure);
    return () => { disconnect(); window.removeEventListener('resize', measure); document.documentElement.style.removeProperty('--game-overview-height'); };
  }, [investigator, part]);
  const counts = boardCounts(state.locations);
  const boards = enabledBoards(state);
  const difficulty = getDifficultyPresentation(state);
  const tracks: Array<[string, string]> = [
    ['Terror', `${state.terrorLevel ?? 0}/10`],
    ['Gates', `${counts.gates}/${state.maximumGatesAllowed ?? '—'}`],
    ['Seals', `${counts.seals}/6`],
    ['Monsters', `${counts.monsters}/${state.maximumMonstersInArkham ?? '—'}`],
    ['Outskirts', `${counts.outskirts}/${state.outskirtsLimit ?? '—'}`],
  ];
  const renderTrack = ([label, value]: [string, string], compact = false) => <button type="button"
    className={`game-track${compact ? ' game-track-compact' : ''}${compact && label === 'Seals' && !sealsFit ? ' game-track-measure' : ''}`}
    key={label} onClick={onTracks} aria-hidden={(compact && label === 'Seals' && !sealsFit) || undefined}
    tabIndex={compact && label === 'Seals' && !sealsFit ? -1 : undefined}>
    {(label === 'Gates' || label === 'Rifts' || trackArtwork[label]) && <ArtworkImage className={`game-track-artwork${label === 'Gates' ? ' gate-back-artwork' : ''}${circularTrackArtwork.has(label) ? ' game-track-artwork-circular' : ''}`} src={label === 'Gates' ? gateBackPath : label === 'Rifts' ? riftArtwork : artworkStyle === 'Digital' ? digitalTrackArtwork[label] ?? trackArtwork[label] : trackArtwork[label]} alt="" aria-hidden="true" />}
    <span>{label}</span><b>{value}</b>
  </button>;
  if (boards.dunwich) tracks.push(['Dunwich', `${state.dunwichHorrorTerrorTrack ?? 0}/3`]);
  if (boards.innsmouth) {
    tracks.push(['Deep One', `${state.deepOnesRisingTrack ?? 0}/6`]);
    const feds = state.fedsRaidInnsmouthTrack;
    tracks.push(['Feds', `${feds?.factoryDistrict ?? 0}/${feds?.churchGreen ?? 0}/${feds?.innsmouthShore ?? 0}`]);
  }
  if (boards.kingsport) tracks.push(['Rifts', [1,2,3].map(id => {
    const rift = state.riftProgress?.find(rift => rift.id === id);
    return rift?.isOpen ? 'Open' : (rift?.tracks ?? []).reduce((sum, track) => sum + (track.locations?.length ?? 0), 0);
  }).join('/')]);
  const actor = state.availableActions?.find(action => action.investigatorName && action.cardType !== 'Investigator')?.investigatorName;
  const turnStatus = state.expectedPlayersCount > 1
    ? state.activePlayer
    : undefined;
  const running = ['WaitingForAction', 'WaitingForActions', 'InProgress', 'In Progress', 'Running'].includes(state.status);
  const turnStatusLabel = running && turnStatus
    ? `${turnStatus}'s turn`
    : undefined;
  const statusLabel = state.status === 'Finished' ? 'Game finished' : turnStatusLabel
    ?? (state.activePlayer === playerName ? 'Your action' : state.activePlayer ? `Waiting for ${state.activePlayer}` : running ? undefined : state.status);
  return <>{part !== 'investigator' && <section className={`game-status-summary game-summary-${part}`} aria-label={part === 'status' ? 'Game status' : 'Game overview'}
    inert={part === 'status' && mobileActions?.open ? true : undefined}>
    {part !== 'tracks' && <div className={`game-status-content${mobileActions ? ' game-status-actions' : ''}${mobileActions?.pending ? ' game-status-turn-ready' : ''}`}
      role={mobileActions ? 'button' : undefined} tabIndex={mobileActions ? 0 : undefined}
      aria-expanded={mobileActions?.open} aria-label={mobileActions ? 'Open actions' : undefined} aria-haspopup={mobileActions ? 'dialog' : undefined}
      onClick={mobileActions?.onOpen} onKeyDown={mobileActions ? event => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); mobileActions.onOpen(); }
      } : undefined}>
    <div className="game-phase-line">
      {state.currentTurn !== undefined && <span>{state.currentTurn === 0 ? 'Setup' : `Round ${state.currentTurn}`}</span>}
      <strong>{state.currentPhase}</strong>
      {actor && <span>{actor}</span>}
      {mobileActions?.pending ? <span className="game-turn-status">Your Turn</span>
        : statusLabel && <span className="game-turn-status">{statusLabel}</span>}
    </div>
    {mobileActions && <div className="game-status-message" aria-label="Current game message"><span>{mobileActions.message?.trim() || statusLabel || 'Waiting for actions.'}</span></div>}
    </div>}
    {part !== 'status' && <div ref={tracksRef} className={`game-track-grid${collapsed ? ' game-tracks-collapsed' : ''}`}>
      {state.ancientOne && <button type="button" className="game-track ancient-summary" onClick={onAncientOne}>
        <ArtworkImage className="game-track-artwork game-track-artwork-circular" src="/eye.jpg" alt="" aria-hidden="true" />
        <span>{state.ancientOne.name}</span><b>Doom {state.ancientOne.doomTrack}/{state.ancientOne.maximumDoomTrack}</b>
      </button>}
      {collapsed && tracks.slice(0, 3).map(track => renderTrack(track, true))}
      <span id={tracksId} className="game-extra-tracks" hidden={!!state.ancientOne && !expanded}>
      {state.ancientOne && ancientOneCounters(state.ancientOne).map(([label, value]) => <button type="button" className="game-track" key={label} onClick={onAncientOne}><span>{label}</span><b>{value}</b></button>)}
      {difficulty && <button type="button" className="game-track difficulty-summary" aria-label={`Difficulty: ${difficulty.name}`} onClick={onTracks}><span>Difficulty</span><b>{difficulty.name}</b></button>}
      {tracks.filter(([label]) => !collapsed || !['Terror', 'Gates', 'Seals'].includes(label)).map(track => renderTrack(track))}
      </span>
      {state.ancientOne && collapsible && <button type="button" className="game-tracks-toggle"
        aria-label={expanded ? 'Collapse game stats' : 'Expand game stats'} aria-expanded={expanded} aria-controls={tracksId}
        onClick={onToggleTracks ?? (() => setLocalExpanded(value => !value))}>{expanded ? '<' : '>'}</button>}
    </div>}
  </section>}
    {(part === 'all' || part === 'investigator') && investigator && <div ref={investigatorRef} className={`game-investigator-controls${flowInvestigator ? ' game-investigator-flow' : ''}`}>
      <InvestigatorInspectButton investigator={investigator} onOpen={onInvestigators} />
    </div>}
  </>;
};
